import { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownOptions, InputFieldNumber, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import { TreatmentPlanTemplateResponse, WeekDateRangeResponse } from '../../api/generated/api';
import { formatDate } from '../../utils/dateUtils';
import styles from './AddTreatmentPlanModal.module.css';

interface AddTreatmentPlanModalProps {
  treatmentPlanTemplateId: string;
  weekNumber: number;
  mode: 'single' | 'series';
  weeks: WeekDateRangeResponse[];
  treatmentPlanTemplates: TreatmentPlanTemplateResponse[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    startWeekNumber: number,
    capacity: number,
    frequency: number,
    occurrences: number,
    endWeekNumber: number
  ) => void;
}

const FREQUENCY_OPTIONS = [1, 2, 3, 4, 5];

export default function AddTreatmentPlanModal({
  treatmentPlanTemplateId,
  weekNumber,
  mode,
  weeks,
  treatmentPlanTemplates,
  isOpen,
  onClose,
  onConfirm
}: AddTreatmentPlanModalProps) {
  const { t, i18n } = useTranslation();

  const [capacity, setCapacity] = useState<number>(0);
  const [frequency, setFrequency] = useState<number>(1);
  const [occurrences, setOccurrences] = useState<number>(0);
  const [startWeek, setStartWeek] = useState<WeekDateRangeResponse | null>(null);
  const [endWeek, setEndWeek] = useState<WeekDateRangeResponse | null>(null);

  const capacityRef = useRef<HTMLDivElement>(null);

  const resetForm = () => {
    setCapacity(0);
    setFrequency(1);
    setOccurrences(0);
    setStartWeek(null);
    setEndWeek(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const templateName = useMemo(() => {
    const template = treatmentPlanTemplates.find((tpt) => tpt.id === treatmentPlanTemplateId);
    return template?.name ?? '';
  }, [treatmentPlanTemplates, treatmentPlanTemplateId]);

  const defaultStartWeek = useMemo(
    () => weeks.find((w) => w.weekNumber === weekNumber) ?? null,
    [weeks, weekNumber]
  );

  const defaultEndWeek = useMemo(
    () => (weeks.length > 0 ? weeks[weeks.length - 1] : null),
    [weeks]
  );

  const activeStartWeek = startWeek ?? defaultStartWeek;
  const activeEndWeek = endWeek ?? defaultEndWeek;

  const weekDisplay = (week: WeekDateRangeResponse) => {
    const label = t('add-treatment-plan-batch-week-display', { weekNumber: week.weekNumber });
    return (
      <>
        {label}{' '}
        <span style={{ color: 'var(--color-text-secondary)' }}>
          {formatDate(week.startDate, i18n.language)} - {formatDate(week.endDate, i18n.language)}
        </span>
      </>
    );
  };

  const modalTitle =
    mode === 'single'
      ? t('add-treatment-plan-batch-title-single')
      : t('add-treatment-plan-batch-title-series');

  const handleConfirm = () => {
    if (!activeStartWeek || !capacity) return;

    if (mode === 'single') {
      const lastWeek = weeks[weeks.length - 1];
      onConfirm(
        activeStartWeek.weekNumber,
        capacity,
        1,
        1,
        lastWeek?.weekNumber ?? activeStartWeek.weekNumber
      );
    } else if (activeEndWeek && occurrences) {
      onConfirm(
        activeStartWeek.weekNumber,
        capacity,
        frequency,
        occurrences,
        activeEndWeek.weekNumber
      );
    }
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={modalTitle}
      onClose={handleClose}
      width={568}
      footer={
        <div className={styles.footer}>
          <Button type="secondary" onClick={handleClose}>
            {t('add-treatment-plan-batch-cancel')}
          </Button>
          <Button
            type="primary"
            disabled={!capacity || (mode === 'series' && !occurrences)}
            onClick={handleConfirm}
          >
            {mode === 'single'
              ? t('add-treatment-plan-batch-confirm-single')
              : t('add-treatment-plan-batch-confirm')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.templateRow}>
          <p className={styles.templateLabel}>{t('add-treatment-plan-batch-template-label')}</p>
          <p className={styles.templateName}>{templateName}</p>
        </div>

        {mode === 'single' ? (
          <>
            <Dropdown
              label={t('add-treatment-plan-batch-start-week-label')}
              selectedOption={activeStartWeek ? weekDisplay(activeStartWeek) : undefined}
            >
              <DropdownOptions
                options={weeks}
                selectedOption={activeStartWeek}
                getKey={(week) => week.weekNumber}
                displayOption={(week) => weekDisplay(week)}
                handleChange={(value) => setStartWeek(value)}
              />
            </Dropdown>

            <div ref={capacityRef}>
              <InputFieldNumber
                label={t('add-treatment-plan-batch-capacity-label')}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value ? Number(e.target.value) : 0)}
              />
            </div>
          </>
        ) : (
          <>
            <div ref={capacityRef}>
              <InputFieldNumber
                label={t('add-treatment-plan-batch-capacity-label')}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value ? Number(e.target.value) : 0)}
              />
            </div>

            <InputFieldNumber
              label={t('add-treatment-plan-batch-occurrences-label')}
              value={occurrences}
              onChange={(e) => setOccurrences(e.target.value ? Number(e.target.value) : 0)}
            />

            {occurrences != null && occurrences > 0 && (
              <Dropdown
                label={t('add-treatment-plan-batch-frequency-label')}
                selectedOption={t(`add-treatment-plan-batch-frequency-${frequency}`)}
              >
                <DropdownOptions
                  options={FREQUENCY_OPTIONS}
                  selectedOption={frequency}
                  getKey={(option) => option}
                  displayOption={(option) => t(`add-treatment-plan-batch-frequency-${option}`)}
                  handleChange={(value) => setFrequency(value)}
                />
              </Dropdown>
            )}

            <Dropdown
              label={t('add-treatment-plan-batch-start-week-label')}
              selectedOption={activeStartWeek ? weekDisplay(activeStartWeek) : undefined}
            >
              <DropdownOptions
                options={weeks}
                selectedOption={activeStartWeek}
                getKey={(week) => week.weekNumber}
                displayOption={(week) => weekDisplay(week)}
                handleChange={(value) => setStartWeek(value)}
              />
            </Dropdown>

            {occurrences != null && occurrences > 1 && (
              <Dropdown
                label={t('add-treatment-plan-batch-end-week-label')}
                selectedOption={activeEndWeek ? weekDisplay(activeEndWeek) : undefined}
              >
                <DropdownOptions
                  options={weeks}
                  selectedOption={activeEndWeek}
                  getKey={(week) => week.weekNumber}
                  displayOption={(week) => weekDisplay(week)}
                  handleChange={(value) => setEndWeek(value)}
                />
              </Dropdown>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
